import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AppProvider, useApp } from "@/contexts/AppContext";
import { PublicLayout, AppLayout } from "@/components/Layout";
import { Home, CekBooking, Armada, Contact } from "@/pages/PublicPages";
import BookingWizard from "@/pages/BookingWizard";
import AboutPage from "@/pages/AboutPage";
import OtherServicesPage from "@/pages/OtherServicesPage";
import InvoicePage from "@/pages/InvoicePage";
import ReviewPage from "@/pages/ReviewPage";
import { AdminBookings } from "@/pages/admin/AdminBookings";
import { AdminReports } from "@/pages/admin/AdminReports";
import { AdminSettings } from "@/pages/admin/AdminSettings";
import { AdminDispatch } from "@/pages/admin/AdminDispatch";
import { AdminFleet } from "@/pages/admin/AdminFleet";
import { DriverToday, DriverHistory } from "@/pages/driver/DriverTrips";
import { Login, Register } from "@/pages/AuthPages";
import { CustomerDashboard, CustomerBookings, BookingDetail, LiveTracking, PaymentSuccess, PaymentCancel, CustomerProfile } from "@/pages/CustomerPages";
import { PartnerDashboard, PartnerNewBooking, PartnerMyBooking, PartnerCustomers, PartnerInvoice, PartnerReports } from "@/pages/PartnerPages";
import { DriverNavigation } from "@/pages/DriverPages";
import {
  AdminDashboard, AdminDrivers,
  AdminCustomers, AdminPartners, AdminFinance, AdminCRM,
} from "@/pages/AdminPages";

function Guard({ role, children }) {
  const { user } = useApp();
  if (user === undefined) return <div className="min-h-screen grid place-items-center text-slate-500">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== role) return <Navigate to={`/${user.role}`} replace />;
  return children;
}

function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/booking" element={<BookingWizard />} />
            <Route path="/invoice/:code" element={<InvoicePage />} />
            <Route path="/ulasan/:token" element={<ReviewPage />} />
            <Route path="/cek-booking" element={<CekBooking />} />
            <Route path="/armada" element={<Armada />} />
            <Route path="/tentang" element={<AboutPage />} />
            <Route path="/layanan-lainnya" element={<OtherServicesPage />} />
            <Route path="/contact" element={<Contact />} />
          </Route>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />

          <Route path="/customer" element={<Guard role="customer"><AppLayout role="customer" /></Guard>}>
            <Route index element={<CustomerDashboard />} />
            <Route path="bookings" element={<CustomerBookings />} />
            <Route path="bookings/:id" element={<BookingDetail />} />
            <Route path="tracking" element={<LiveTracking />} />
            <Route path="profile" element={<CustomerProfile />} />
            <Route path="payment/success" element={<PaymentSuccess />} />
            <Route path="payment/cancel" element={<PaymentCancel />} />
          </Route>

          <Route path="/partner" element={<Guard role="partner"><AppLayout role="partner" /></Guard>}>
            <Route index element={<PartnerDashboard />} />
            <Route path="new" element={<PartnerNewBooking />} />
            <Route path="bookings" element={<PartnerMyBooking />} />
            <Route path="customers" element={<PartnerCustomers />} />
            <Route path="invoice" element={<PartnerInvoice />} />
            <Route path="reports" element={<PartnerReports />} />
          </Route>

          <Route path="/driver" element={<Guard role="driver"><AppLayout role="driver" /></Guard>}>
            <Route index element={<DriverToday />} />
            <Route path="navigation" element={<DriverNavigation />} />
            <Route path="history" element={<DriverHistory />} />
          </Route>

          <Route path="/admin" element={<Guard role="admin"><AppLayout role="admin" /></Guard>}>
            <Route index element={<AdminDashboard />} />
            <Route path="bookings" element={<AdminBookings />} />
            <Route path="dispatch" element={<AdminDispatch />} />
            <Route path="fleet" element={<AdminFleet />} />
            <Route path="drivers" element={<AdminDrivers />} />
            <Route path="customers" element={<AdminCustomers />} />
            <Route path="partners" element={<AdminPartners />} />
            <Route path="finance" element={<AdminFinance />} />
            <Route path="reports" element={<AdminReports />} />
            <Route path="crm" element={<AdminCRM />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AppProvider>
  );
}

export default App;
